import React, { useRef, useEffect, useCallback, useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Paper, 
  Fade, 
  CircularProgress, 
  Fab, 
  Zoom 
} from '@mui/material';
import { 
  Close as CloseIcon, 
  SmartToy as RobotIcon, 
  Chat as ChatIcon,
  OpenInFull as ExpandIcon,
  CloseFullscreen as CompressIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import {
  toggleChat,
  addUserMessage,
  startAIMessage,
  clearFileContext,
} from '../../redux/aiChat/aiChatSlice';
import { fetchChatHistory, sendChatMessage } from '../../redux/aiChat/aiChatThunk';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { LoadState } from '../../constants/enums';
import { useAuth } from '../../contexts/AuthProvider/AuthProvider';

// ─── Styled Components (Merged) ────────────────────────────────────────────────

const WindowContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded?: boolean }>(({ theme, isExpanded }) => ({
  position: 'fixed',
  bottom: '80px',
  right: '15px',
  width: isExpanded ? 1200 : 380,
  height: '80%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 20px 40px rgba(11, 28, 48, 0.12)',
  zIndex: 1000,
  backgroundColor: '#f8f9ff',
  transition: theme.transitions.create(['width'], {
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down('sm')]: {
    bottom: 0,
    right: 0,
    width: '100%',
    height: '100%',
    borderRadius: 0,
  },
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: '#97c96a',
  color: 'black',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
}));

const MessagesArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#d3e4fe',
    borderRadius: '10px',
  },
}));

const TypingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: '#ffffff',
  borderRadius: '16px',
  width: 'fit-content',
  alignSelf: 'flex-start',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: '15px',
  right: '80px',
  zIndex: 1001,
  backgroundColor: '#97c96a',
  color: 'black',
  boxShadow: '0 8px 16px rgba(0, 88, 190, 0.3)',
  transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
    backgroundColor: '#97c96a',
  },
}));

const PENDING_AI_ID_KEY = '__pending_ai_id__';

const ChatWidget: React.FC = () => {
  const { messages, loading, isOpen, history, fileContext } = useSelector((state: RootState) => state.aiChat);
  const { user } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);

  // Refs for smart scroll during pagination
  const scrollSnapRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const lastMessagesCountRef = useRef(0);
  const firstMessageIdRef = useRef<string | null>(null);
  const isInitialLoad = history.loading === 0; // LoadState.NotLoaded
  const [isExpanded, setIsExpanded] = useState(false);

  /** Fetch history via Redux thunk */
  const fetchHistory = useCallback(
    (pageNo: number) => {
      dispatch(fetchChatHistory({ pageNo }));
    },
    [dispatch],
  );

  // Fetch initial history whenever chat is opened for the first time
  useEffect(() => {
    if (isOpen && history.loading === 0) {
      fetchHistory(1);
    }
  }, [isOpen, history.loading, fetchHistory]);

  const handleLoadMore = () => {
    if (messagesAreaRef.current) {
      scrollSnapRef.current = {
        scrollHeight: messagesAreaRef.current.scrollHeight,
        scrollTop: messagesAreaRef.current.scrollTop,
      };
    }
    fetchHistory(history.page + 1);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!messagesAreaRef.current) return;

    const currentFirstId = messages[0]?.id;

    // SCROLL LOGIC:
    if (isInitialLoad && messages.length > 0) {
      scrollToBottom();
    } else if (currentFirstId !== firstMessageIdRef.current && scrollSnapRef.current) {
      const { scrollHeight, scrollTop } = scrollSnapRef.current;
      const newScrollHeight = messagesAreaRef.current.scrollHeight;
      const heightDifference = newScrollHeight - scrollHeight;
      messagesAreaRef.current.scrollTop = scrollTop + heightDifference;
      scrollSnapRef.current = null;
    } else if (messages.length > lastMessagesCountRef.current || loading) {
      scrollToBottom();
    }

    firstMessageIdRef.current = currentFirstId;
    lastMessagesCountRef.current = messages.length;
  }, [messages, loading, isInitialLoad]);

  const handleSendMessage = async (content: string, ctxOverride?: string | null) => {
    const pendingId = PENDING_AI_ID_KEY + Date.now();
    // Use the fileContext from Redux (or override passed from ChatInput)
    const ctx = ctxOverride !== undefined ? ctxOverride : fileContext;
    // What the API receives: include the full URL so AI can fetch and read the document
    const messageWithCtx = ctx
      ? `The user wants to ask about a document. Please read and review the file at this URL before answering.\ndocument_url: ${ctx}\n\nUser question: ${content}`
      : content;
    // What the chat UI shows: clean filename + user question
    const displayMessage = ctx
      ? `📎 ${ctx.split('/').pop()}\n${content}`
      : content;

    dispatch(addUserMessage(displayMessage));
    dispatch(startAIMessage({ id: pendingId }));
    (window as any).__pendingAiMessageId__ = pendingId;

    dispatch(sendChatMessage({ message: messageWithCtx, pendingId }));
    if (ctx) dispatch(clearFileContext());
  };

  // Only render if user is logged in
  if (!user || !user.isLogin) return null;

  return (
    <>
      <Zoom in={true}>
        <StyledFab 
          aria-label="chat" 
          onClick={() => dispatch(toggleChat())}
        >
          {isOpen ? <CloseIcon /> : <ChatIcon />}
        </StyledFab>
      </Zoom>

      <Fade in={isOpen}>
        <WindowContainer elevation={0} isExpanded={isExpanded} sx={{ display: isOpen ? 'flex' : 'none' }}>
          <Header>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <RobotIcon sx={{ fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: 'Montserrat, sans-serif' }}>
                  AI Assistant
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: -0.5 }}>
                  Online
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton 
                size="small" 
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ color: 'white' }}
              >
                {isExpanded ? <CompressIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => dispatch(toggleChat())}
                sx={{ color: 'white' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Header>

          <MessagesArea ref={messagesAreaRef}>
            {history.hasMore && history.loading !== LoadState.NotLoaded && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                {history.loading === LoadState.InProgress ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      cursor: 'pointer', 
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onClick={handleLoadMore}
                  >
                    Load previous messages
                  </Typography>
                )}
              </Box>
            )}

            {history.loading === LoadState.InProgress && isInitialLoad && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {history.loading === LoadState.Failed && history.error && (
              <Box sx={{ p: 2, textAlign: 'center', opacity: 0.6 }}>
                <Typography variant="caption" color="error">{history.error}</Typography>
              </Box>
            )}
            {history.loading === LoadState.Loaded && messages.length === 0 && !loading && (
              <Box sx={{ p: 2, textAlign: 'center', opacity: 0.6 }}>
                <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif' }}>
                  How can I help you today?
                </Typography>
              </Box>
            )}
            {messages.map((msg) => (
              <ChatMessage key={msg.id} message={msg} />
            ))}
            
            {loading && !messages.some(m => m.isStreaming) && (
              <TypingIndicator>
                <CircularProgress size={12} thickness={5} />
                <Typography variant="caption" color="text.secondary">
                  Connecting to AI...
                </Typography>
              </TypingIndicator>
            )}
            <div ref={messagesEndRef} />
          </MessagesArea>

          <ChatInput onSendMessage={handleSendMessage} disabled={loading} />
        </WindowContainer>
      </Fade>
    </>
  );
};

export default ChatWidget;
